import { SALES_TAX_EXEMPTION_STATES, StateEntry } from './us-states';

/**
 * Pure logic behind `<app-sales-tax-states-matrix>`.
 *
 * This lives outside the component so it can be exercised by
 * `npm run smoke:sales-tax-states` without an Angular TestBed. The rules it
 * encodes are invisible at runtime — a wrong cell kind produces a grid that
 * looks plausible and silently submits the wrong `exemptStates` — so they are
 * worth pinning. Canonical spec:
 * `docs/contracts/shared/SalesTaxExemption.md` §State-matrix UX.
 */

/** Visual states a single cell can be in. Drives styling and tooltips. */
export type StateMatrixCellKind =
  | 'no-nexus'       // SP does not collect tax here — exemption moot.
  | 'reciprocal'     // SP recognises the issuing state's certificate here.
  | 'non-reciprocal' // SP collects tax here; user's cert is NOT recognised → warn.
  | 'issuing'        // The certificate was issued by this state.
  | 'plain';         // SP collects tax here, no special relationship.

export interface StateMatrixCell {
  code: string;
  name: string;
  kind: StateMatrixCellKind;
  selected: boolean;
  /** True when the cell's `kind === 'non-reciprocal'` and the user has selected it. */
  warned: boolean;
}

export type SalesTaxMatrixMode = 'exemption' | 'nexus' | 'reciprocity';

/** Structural mirror of `SalesTaxExemptionType`, declared here to keep this
 *  module free of any Angular-service import. */
export type SalesTaxExemptionTypeLike = 'wholesale' | 'other' | 'non-exempt';

/** Structural mirror of the fields of `SalesTaxConfig` this module reads. */
export interface SalesTaxReciprocityConfigLike {
  nexusStates?: string[];
  reciprocityOther?: string[];
  reciprocityWholesale?: string[];
}

export interface BuildStateMatrixOptions {
  mode: SalesTaxMatrixMode;
  config: SalesTaxReciprocityConfigLike | null;
  type: SalesTaxExemptionTypeLike;
  issuingState: string;
  selectedStates: ReadonlyArray<string>;
  /**
   * Replace the current selection with the default claim rather than preserve
   * it. Set when the basis of the claim (type / issuing state / first arrival
   * of the SP config) changed and the previous seed is therefore stale.
   */
  reseedDefaults?: boolean;
}

/** The reciprocity list that applies to an exemption type. `non-exempt` has none. */
export function reciprocityStatesFor(
  config: SalesTaxReciprocityConfigLike | null,
  type: SalesTaxExemptionTypeLike
): string[] {
  if (!config) { return []; }
  if (type === 'wholesale') { return (config.reciprocityWholesale || []).slice(); }
  if (type === 'other') { return (config.reciprocityOther || []).slice(); }
  return [];
}

/**
 * The claim a certificate supports: its issuing state unioned with every state
 * the SP has recorded reciprocity with for this exemption type.
 *
 * This is deliberately independent of `nexusStates`. The union is a fact about
 * the certificate; nexus is a fact about the Service Provider. Over-claiming in
 * a no-nexus state is inert downstream (no tax is computed there at all),
 * whereas under-claiming in a nexus state charges tax the certificate exempts —
 * so the asymmetry runs in favour of including the whole union.
 */
export function defaultClaimStates(
  config: SalesTaxReciprocityConfigLike | null,
  type: SalesTaxExemptionTypeLike,
  issuingState: string
): string[] {
  const issuing = (issuingState || '').toUpperCase();
  if (!issuing) { return []; }
  const claim = new Set<string>([issuing]);
  for (const c of reciprocityStatesFor(config, type)) {
    claim.add(String(c || '').toUpperCase());
  }
  // Emit in matrix order so the result is stable and comparable.
  return (SALES_TAX_EXEMPTION_STATES as ReadonlyArray<StateEntry>)
    .map(s => s.code)
    .filter(code => claim.has(code));
}

/**
 * Classify and select every cell of the matrix.
 *
 * Cell-kind precedence is `issuing` > `reciprocal` > `no-nexus` >
 * `non-reciprocal` > `plain`. The first two outrank `no-nexus` deliberately —
 * see {@link defaultClaimStates}. Evaluating `no-nexus` first (as the component
 * did until 2026-10-20) rendered the issuing state grey/disabled and dropped it
 * from the submitted `exemptStates` whenever the SP had no nexus there, which
 * is the ordinary case: a certificate is issued where the *organization*
 * operates, not where the SP collects.
 */
export function buildStateMatrixCells(opts: BuildStateMatrixOptions): StateMatrixCell[] {
  const nexus = new Set((opts.config && opts.config.nexusStates) || []);
  const reciprocity = new Set(reciprocityStatesFor(opts.config, opts.type));
  const selected = new Set((opts.selectedStates || []).map(c => String(c || '').toUpperCase()));
  const issuing = (opts.issuingState || '').toUpperCase();

  // If the SP hasn't published any nexus states yet (or the config simply
  // hasn't loaded), we cannot tell which states are no-nexus. In that case
  // treat every state as `plain` so the matrix is fully interactive rather
  // than disabled-everywhere (which looked broken).
  const nexusKnown = nexus.size > 0;

  // Seed the default claim when the basis changed, or when the caller has not
  // expressed a selection yet (first load of an org with no exemption on file).
  const wantsDefault = opts.mode === 'exemption' && !!issuing
    && (!!opts.reseedDefaults || selected.size === 0);

  return (SALES_TAX_EXEMPTION_STATES as ReadonlyArray<StateEntry>).map(s => {
    const code = s.code;
    let kind: StateMatrixCellKind = 'plain';

    if (opts.mode === 'exemption') {
      if (issuing && code === issuing) {
        kind = 'issuing';
      } else if (issuing && reciprocity.has(code)) {
        kind = 'reciprocal';
      } else if (nexusKnown && !nexus.has(code)) {
        kind = 'no-nexus';
      } else if (issuing) {
        // SP collects tax here AND the user has an issuing state, but this
        // state is not in the reciprocity set → cert is NOT recognised.
        kind = 'non-reciprocal';
      } else {
        kind = 'plain';
      }
    }

    // The default claim is exactly {issuingState} ∪ reciprocity[type].
    // Assigning rather than OR-ing matters on a re-seed: the stale set from the
    // previous type must be replaced, not merged into.
    let isSelected = wantsDefault
      ? (kind === 'issuing' || kind === 'reciprocal')
      : selected.has(code);

    // The issuing state is part of the claim for as long as a certificate is on
    // file and can never be dropped — including on a record saved before the
    // seeding bug above was fixed, which self-heals on the next save.
    if (kind === 'issuing') {
      isSelected = true;
    }

    return {
      code,
      name: s.name,
      kind,
      selected: isSelected,
      warned: isSelected && kind === 'non-reciprocal',
    };
  });
}

/** Codes of the currently selected cells, in matrix order. */
export function selectedCodes(cells: ReadonlyArray<StateMatrixCell>): string[] {
  return cells.filter(c => c.selected).map(c => c.code);
}
